/**
 * Composant de spinner de chargement
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche un indicateur de chargement avec différentes tailles et couleurs.
 */

  // Client Component pour Next.js

/**
 * Taille du spinner
 */
export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Variante de couleur du spinner
 */
export type SpinnerVariant = 'primary' | 'secondary' | 'white' | 'gray';

/**
 * Props du composant LoadingSpinner
 */
export interface LoadingSpinnerProps {
  /** Taille du spinner */
  size?: SpinnerSize;
  /** Variante de couleur */
  variant?: SpinnerVariant;
  /** Message à afficher sous le spinner */
  message?: string;
  /** Centrer le spinner */
  centered?: boolean;
  /** Afficher en plein écran */
  fullScreen?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Map des tailles
 */
const SIZE_CLASSES = {
  xs: 'w-3 h-3 border-2',
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-3',
  xl: 'w-12 h-12 border-4',
} as const;

/**
 * Map des couleurs
 */
const VARIANT_CLASSES = {
  primary: 'border-primary border-t-transparent',
  secondary: 'border-border-strong border-t-transparent',
  white: 'border-white border-t-transparent',
  gray: 'border-gray-400 border-t-transparent',
} as const;

/**
 * Composant LoadingSpinner
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isLoading } = useAuth();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner message="Chargement en cours..." />;
 *   }
 *
 *   return <div>Contenu</div>;
 * }
 * ```
 */
export function LoadingSpinner({
  size = 'md',
  variant = 'primary',
  message,
  centered = false,
  fullScreen = false,
  className = '',
}: LoadingSpinnerProps) {
  const sizeClass = SIZE_CLASSES[size];
  const variantClass = VARIANT_CLASSES[variant];

  const spinner = (
    <div
      className={`${sizeClass} ${variantClass} rounded-full animate-spin ${className}`}
      role="status"
      aria-label="Chargement en cours"
    />
  );

  // Mode plein écran
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-surface/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="flex flex-col items-center">
          {spinner}
          {message && (
            <p className="mt-4 text-sm text-content-muted">
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Mode centré
  if (centered) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        {spinner}
        {message && (
          <p className="mt-3 text-sm text-content-muted">
            {message}
          </p>
        )}
      </div>
    );
  }

  // Mode inline
  if (message) {
    return (
      <div className="inline-flex items-center">
        {spinner}
        <span className="ml-2 text-sm text-content-muted">
          {message}
        </span>
      </div>
    );
  }

  return spinner;
}

/**
 * Composant de spinner pour boutons
 *
 * @example
 * ```tsx
 * <button disabled={isLoading}>
 *   {isLoading ? <ButtonSpinner /> : 'Connexion'}
 * </button>
 * ```
 */
export function ButtonSpinner({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin h-4 w-4 ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label="Chargement"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Composant de skeleton loader (placeholder de contenu)
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { user, isLoading } = useAuth();
 *
 *   if (isLoading) {
 *     return (
 *       <div>
 *         <SkeletonLoader className="h-12 w-12 rounded-full" />
 *         <SkeletonLoader className="h-4 w-32 mt-2" />
 *         <SkeletonLoader className="h-3 w-48 mt-1" />
 *       </div>
 *     );
 *   }
 *
 *   return <div>{user?.nom}</div>;
 * }
 * ```
 */
export function SkeletonLoader({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-surface-overlay rounded ${className}`}
      role="status"
      aria-label="Chargement du contenu"
    />
  );
}

/**
 * Composant de dots loader (3 points animés)
 *
 * @example
 * ```tsx
 * <DotsLoader variant="primary" />
 * ```
 */
export function DotsLoader({
  variant = 'primary',
  className = '',
}: {
  variant?: SpinnerVariant;
  className?: string;
}) {
  const dotColorClass =
    variant === 'primary'
      ? 'bg-primary'
      : variant === 'white'
      ? 'bg-white'
      : variant === 'gray'
      ? 'bg-gray-400'
      : 'bg-border-strong';

  return (
    <div className={`flex items-center space-x-1 ${className}`} role="status" aria-label="Chargement">
      <div
        className={`w-2 h-2 ${dotColorClass} rounded-full animate-bounce`}
        style={{ animationDelay: '0ms' }}
      />
      <div
        className={`w-2 h-2 ${dotColorClass} rounded-full animate-bounce`}
        style={{ animationDelay: '150ms' }}
      />
      <div
        className={`w-2 h-2 ${dotColorClass} rounded-full animate-bounce`}
        style={{ animationDelay: '300ms' }}
      />
    </div>
  );
}

/**
 * Composant de barre de progression
 *
 * @example
 * ```tsx
 * <ProgressBar progress={75} message="Téléchargement en cours..." />
 * ```
 */
export function ProgressBar({
  progress,
  message,
  variant = 'primary',
  className = '',
}: {
  progress: number;
  message?: string;
  variant?: SpinnerVariant;
  className?: string;
}) {
  const barColorClass =
    variant === 'primary'
      ? 'bg-primary'
      : variant === 'white'
      ? 'bg-white'
      : variant === 'gray'
      ? 'bg-gray-400'
      : 'bg-border-strong';

  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={className} role="progressbar" aria-valuenow={clampedProgress} aria-valuemin={0} aria-valuemax={100}>
      {message && (
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-content-muted">{message}</span>
          <span className="text-sm font-medium text-content">
            {Math.round(clampedProgress)}%
          </span>
        </div>
      )}
      <div className="w-full bg-surface-overlay rounded-full h-2 overflow-hidden">
        <div
          className={`${barColorClass} h-full transition-all duration-300 ease-out`}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
}
